/* eslint-disable no-restricted-syntax */
import Logger, { Log } from '@/includes/service/Log.service';
import { GenericObject, Schema } from '@/includes/definitions/index.d';

type TKey = string | number | IDBKeyRange;
type DateRange = { startDate?: Date, endDate?: Date };

export interface LocalModel<T = any> {
  create(data: T | T[]): Promise<T | T[]>;

  update(data: T): Promise<T>;

  delete(key: TKey | TKey[]): Promise<number>;

  get(key: TKey, count?: number): Promise<T>;

  getByMatchingObject(query: GenericObject, dateRange?: DateRange, limit?: number): Promise<T[]>;

  getAll(key?: TKey): Promise<T[]>;
  getByIDs(keys?: TKey[]): Promise<T[]>;
  getWithinDateRange(
    startDate: Date,
    endDate: Date,
    limit: number
  ): Promise<T[]>;

  get isReady(): boolean;
}

export default class LocalDatabase {
  static db: IDBDatabase;

  static verifySupport() {
    return 'indexedDB' in window;
  }

  static async initializeDatabase(schemas: Schema[], database: string, version: number) {
    if (!LocalDatabase.verifySupport()) throw new Error('Browser Unsupported');
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(database, version);
      request.onerror = (e: Event) => {
        Logger.error('[DB]: Error', JSON.stringify(e));
        reject(e);
      };

      request.onsuccess = (e: Event) => {
        Log('[DB]: Successful database connection', JSON.stringify(e));
        LocalDatabase.db = request.result;
        LocalDatabase.db.onversionchange = () => {
          Logger.error('[DB]: Closing this connection - a newer version is being opened elsewhere. Reload this tab.');
          LocalDatabase.db.close();
        };
        resolve(LocalDatabase.db);
      };

      request.onblocked = (e: Event) => {
        Logger.error(
          '[DB]: Upgrade blocked - another tab/connection is still open at an older schema version. '
          + 'Close other tabs of this app (or they will auto-close shortly) and reload.',
          JSON.stringify(e),
        );
      };

      request.onupgradeneeded = (e: Event) => {
        Log('[DB]: Database Created', JSON.stringify(e));
        const db = request.result;
        for (const schema of schemas) {
          if (db.objectStoreNames.contains(schema.name)) continue;
          LocalDatabase.setupStore(schema, db);
        }
      };
    });
  }

  static async setupStore(schema: Schema, db: IDBDatabase) {
    await (new Promise((resolve) => {
      const objectStore = db.createObjectStore(schema.name, {
        keyPath: schema.keyPath,
        autoIncrement: schema.autoIncrement,
      });

      for (const k of Object.keys(schema.schema)) objectStore.createIndex(k, k, schema.schema[k]);
      if (schema.timestamps) {
        objectStore.createIndex('createdAt', 'createdAt', { unique: false });
        objectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      objectStore.transaction.oncomplete = (ev) => {
        Log(`[DB]: ${schema.name} created`, JSON.stringify(ev));
        resolve(ev);
      };
    }));
  }

  static insert<T = GenericObject>(schema: Schema, data: T | T[]): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const transaction = LocalDatabase.db.transaction(schema.name, 'readwrite');
      const objectStore = transaction.objectStore(schema.name);
      const iData: T[] = Array.isArray(data) ? data : [data];
      const responseData = [] as typeof iData;

      transaction.oncomplete = (ev) => {
        Log(`[DB]: ${schema.name} inserts complete`, JSON.stringify(ev));
        resolve(responseData as T[]);
      };

      transaction.onerror = (ev) => Log(`[DB]: ${schema.name} inserts failed`, JSON.stringify(ev));

      for (const entry of iData.map((en) => JSON.parse(JSON.stringify(en)))) {
        if (schema.timestamps) {
          entry.createdAt = new Date().toString();
          entry.updatedAt = new Date().toString();
        }

        const request = objectStore.add(entry);
        request.onsuccess = () => {
          responseData.push({ id: request.result, ...entry });
          Log(`[DB]: New ${schema.name} insert`);
        };

        request.onerror = () => {
          reject(entry);
          Log(`[DB]: Error on ${schema.name} insert`);
        };
      }
    });
  }

  static async update<T = GenericObject>(schema: Schema, data: T): Promise<T> {
    return new Promise((resolve, reject) => {
      const objectStore = LocalDatabase.db
        .transaction(schema.name, 'readwrite')
        .objectStore(schema.name);
      const entry: T = JSON.parse(JSON.stringify(data));
      if (schema.timestamps) entry['updatedAt' as string] = new Date().toString();

      const request = objectStore.put(entry);
      request.onsuccess = () => {
        resolve(entry);
        Log(`[DB]: New ${schema.name} insert`);
      };

      request.onerror = (e) => {
        reject(e);
        Log(`[DB]: Error on ${schema.name} insert`);
      };
    });
  }

  static async delete(schema: Schema, key: TKey | TKey[]): Promise<number> {
    const transaction = LocalDatabase.db.transaction(schema.name, 'readwrite');
    const deleteEntry = (entry: TKey): Promise<boolean> => new Promise((resolve, reject) => {
      const objectStore = transaction.objectStore(schema.name);
      const request = objectStore.delete(entry);
      request.onsuccess = () => {
        Log(`[DB]: New ${schema.name} delete`);
        resolve(true);
      };
      request.onerror = (ev) => {
        Log(`[DB]: Error on ${schema.name} delete, key ${entry}`);
        reject(ev);
      };
    });

    const iKey = Array.isArray(key) ? key : [key];
    return (await Promise.all(iKey.map((k) => deleteEntry(k)))).length;
  }

  static async get<T>(schema: Schema, key: TKey, count = 1): Promise<T> {
    return new Promise((resolve, reject) => {
      const store = LocalDatabase.db
        .transaction(schema.name)
        .objectStore(schema.name);
      const request = count > 1 ? store.getAll(key, count) : store.get(key);
      request.onsuccess = () => {
        Log(`[DB]: Retrieved new ${schema.name} ${count > 1 ? 'entries' : 'entry'}`);
        resolve(request.result);
      };
      request.onerror = (e) => {
        Log(`[DB]: Error retrieving from ${schema.name} with key ${key}`);
        reject(e);
      };
    });
  }

  static async getAll<T>(schema: Schema, key?: TKey): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const request = LocalDatabase.db
        .transaction(schema.name)
        .objectStore(schema.name)
        .getAll(key);

      request.onsuccess = () => {
        resolve(request.result);
        Log(`[DB]: Retrieved all ${schema.name} entries`);
      };

      request.onerror = (e) => {
        reject(e);
        Log(`[DB]: Error retrieving all ${schema.name}  entries`);
      };
    });
  }

  static async getByIds<T>(schema: Schema, ids: TKey[]): Promise<T[]> {
    return new Promise((resolve, reject) => {
      if (ids.length <= 0) {
        resolve([]);
        return;
      }

      const store = LocalDatabase.db
        .transaction(schema.name)
        .objectStore(schema.name);

      const sortedIds = [...ids].sort((a, b) => (+a) - (+b));
      const results: T[] = [];
      let index = 0;

      const request = store.openCursor(IDBKeyRange.bound(sortedIds[0], sortedIds[sortedIds.length - 1]));

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || index >= sortedIds.length) {
          Log(`[DB]: Retrieved ${results.length} ${schema.name} entries by IDs`);
          resolve(results);
          return;
        }

        // Skip over target ids with no matching record: the cursor has already moved past them.
        while (index < sortedIds.length && indexedDB.cmp(cursor.key, sortedIds[index]) > 0) {
          index += 1;
        }

        if (index >= sortedIds.length) {
          Log(`[DB]: Retrieved ${results.length} ${schema.name} entries by IDs`);
          resolve(results);
          return;
        }

        if (indexedDB.cmp(cursor.key, sortedIds[index]) === 0) {
          results.push(cursor.value);
          index += 1;
        }

        cursor.continue();
      };

      request.onerror = (e) => {
        reject(e);
        Log(`[DB]: Error retrieving ${schema.name} entries by IDs`);
      };
    });
  }

  static async getWithinDateRange<T extends GenericObject>(
    schema: Schema,
    startDate: Date,
    endDate: Date,
    limit: number,
  ): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const result: T[] = [];
      const cursorRequest = LocalDatabase.db
        .transaction(schema.name)
        .objectStore(schema.name)
        .openCursor();

      cursorRequest.onsuccess = (ev) => {
        const cursor = (ev.target as GenericObject).result;
        if (result.length >= limit) resolve(result);
        if (cursor) {
          const item: T = cursor.value;
          const createdAt = new Date(item.createdAt);
          if (createdAt >= startDate && createdAt <= endDate) result.push(item);
          cursor.continue();
        } else {
          resolve(result);
        }
      };

      cursorRequest.onerror = (ev) => reject(ev);
    });
  }

  static async getByMatchingObject<T extends GenericObject>(
    schema: Schema,
    query: GenericObject,
    limit: number,
    dateRange?: DateRange,
  ): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const result: T[] = [];
      const cursorRequest = LocalDatabase.db
        .transaction(schema.name)
        .objectStore(schema.name)
        .openCursor();

      cursorRequest.onsuccess = (ev) => {
        const cursor = (ev.target as GenericObject).result;
        if (result.length >= limit) resolve(result);
        if (cursor) {
          const item: T = cursor.value;
          for (const queryProperty of Object.keys(query)) {
            if (!item.hasOwnProperty(queryProperty) || item[queryProperty] !== query[queryProperty]) {
              cursor.continue();
              return;
            }
          }

          if (dateRange?.startDate && item.createdAt < dateRange?.startDate) {
            cursor.continue();
            return;
          }
          if (dateRange?.endDate && item.createdAt > dateRange?.endDate) {
            cursor.continue();
            return;
          }

          result.push(item);
          cursor.continue();
        } else {
          resolve(result);
        }
      };

      cursorRequest.onerror = (ev) => reject(ev);
    });
  }

  static MakeModel<T = GenericObject>(schema: Schema): LocalModel<T> {
    return {
      create: async (data: T | T[]): Promise<T[]> => LocalDatabase.insert<T>(schema, data),
      update: async (data: T) => LocalDatabase.update(schema, data),
      delete: async (key: TKey | TKey[]): Promise<number> => LocalDatabase.delete(schema, key),
      get: async (key: TKey, count = 1): Promise<T> => LocalDatabase.get(schema, key, count),
      getAll: async (key?: TKey): Promise<T[]> => LocalDatabase.getAll<T>(schema, key),
      getByIDs: async (keys?: TKey[]): Promise<T[]> => LocalDatabase.getByIds<T>(schema, keys),
      getByMatchingObject: async (
        query: GenericObject,
        dateRange?: DateRange,
        limit = Number.MAX_SAFE_INTEGER,
      ) => LocalDatabase.getByMatchingObject(schema, query, limit, dateRange),
      getWithinDateRange: async (
        startDate: Date,
        endDate: Date,
        limit = Number.MAX_SAFE_INTEGER,
      ) => LocalDatabase.getWithinDateRange(schema, startDate, endDate, limit),
      get isReady() {
        return !!LocalDatabase.db;
      },
    };
  }
}
